import React, { useState, useMemo, useRef, useEffect } from 'react';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './AvailabilityTable.module.css';
import CalendarIcon from '../../../../images/calendar-icon.svg';
import SearchInput from '../../../common/search-input/SearchInput';
import Pagination from '../../../common/pagination/pagination';
import TabGroup from '../../../tabGroup/TabGroup';
import TrashIcon from '../../../svgIcons/TrashIcon';
import Loader from '../../../common/loader/Loader';
import { datePresetOptions, getLockerColor } from '../../../../constants/constants';
import { eventData } from '../types';

const SEARCH_DEBOUNCE_MS = 2000;

interface AvailabilityTableProps {
    sortedLockers: eventData[];
    handleDeleteClick: (item: eventData) => void;
    onDateRangeChange?: (startDate: string, endDate: string) => void;
}

const getInitialThisWeekRange = (): [Date | null, Date | null] => {
    const now = moment();
    const start = now.clone().startOf('week');
    const end = now.clone().endOf('week');
    return [start.toDate(), end.toDate()];
};

const AvailabilityTable: React.FC<AvailabilityTableProps> = ({ sortedLockers, handleDeleteClick, onDateRangeChange }) => {
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getInitialThisWeekRange);
    const [startDate, endDate] = dateRange;
    const [datePreset, setDatePreset] = useState<string>('This week');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const today = new Date();
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const autoSelectEndDate = () => {
        if (startDate && !endDate) {
            setDateRange([startDate, startDate > today ? startDate : today]);
        }
    };

    const getRangeForPreset = (preset: string): [Date, Date] => {
        const now = moment();

        switch (preset) {
            case 'This week': {
                const start = now.clone().startOf('week');
                const end = now.clone().endOf('week');
                return [start.toDate(), end.toDate()];
            }
            case 'Last week': {
                const start = now.clone().subtract(1, 'week').startOf('week');
                const end = now.clone().subtract(1, 'week').endOf('week');
                return [start.toDate(), end.toDate()];
            }
            case 'This month': {
                const start = now.clone().startOf('month');
                const end = now.clone().endOf('month');
                return [start.toDate(), end.toDate()];
            }
            case 'Last month': {
                const start = now.clone().subtract(1, 'month').startOf('month');
                const end = now.clone().subtract(1, 'month').endOf('month');
                return [start.toDate(), end.toDate()];
            }
            default: {
                const start = now.clone().startOf('month');
                const end = now.clone().endOf('month');
                return [start.toDate(), end.toDate()];
            }
        }
    };

    useEffect(() => {
        const id = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(id);
    }, [searchTerm]);

    const lastSentRangeRef = useRef<string | null>(null);
    useEffect(() => {
        if (!onDateRangeChange || !startDate || !endDate) return;

        const start = moment(startDate).format('YYYY-MM-DD');
        const end = moment(endDate).format('YYYY-MM-DD');
        const key = `${start}_${end}`;
        if (lastSentRangeRef.current === key) return;

        lastSentRangeRef.current = key;
        onDateRangeChange(start, end);
    }, [startDate, endDate, onDateRangeChange]);

    const isSearchDebouncing = searchTerm !== debouncedSearchTerm;

    const filteredRows = useMemo(() => {
        const q = debouncedSearchTerm.trim().toLowerCase();

        const matchesSearch = (row: eventData) => {
            if (!q) return true;

            const unit = row.unitYardiId || String(row.unit_number) || '';
            const locker = row.locker_number != null ? String(row.locker_number) : '';

            return [unit, locker].some((x) => String(x).toLowerCase().includes(q));
        };

        const matchesDateRange = (row: eventData) => {
            if (!startDate || !endDate) return true;
            if (!row.start_date || !row.end_date) return false;

            const filterStart = moment(startDate).startOf('day');
            const filterEnd = moment(endDate).endOf('day');

            const rowStart = moment(row.start_date).startOf('day');
            const rowEnd = moment(row.end_date).endOf('day');

            // It matches if the availability range overlaps with the filter date range
            return rowStart.isSameOrBefore(filterEnd) && rowEnd.isSameOrAfter(filterStart);
        };

        return sortedLockers
            .filter((row) => matchesSearch(row) && matchesDateRange(row))
            .sort((a, b) => {
                const aStart = a.start_date ? moment(a.start_date).valueOf() : 0;
                const bStart = b.start_date ? moment(b.start_date).valueOf() : 0;
                if (aStart !== bStart) return aStart - bStart;
                const aEnd = a.end_date ? moment(a.end_date).valueOf() : 0;
                const bEnd = b.end_date ? moment(b.end_date).valueOf() : 0;
                if (aEnd !== bEnd) return aEnd - bEnd;
                const aLocker = a.locker_number ?? 0;
                const bLocker = b.locker_number ?? 0;
                if (aLocker !== bLocker) return aLocker - bLocker;
                const aUnit = String(a.unitYardiId ?? a.unit_number ?? '');
                const bUnit = String(b.unitYardiId ?? b.unit_number ?? '');
                return aUnit.localeCompare(bUnit, undefined, { numeric: true });
            });
    }, [sortedLockers, startDate, endDate, debouncedSearchTerm]);

    // Paginate filtered rows
    const paginatedRows = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredRows.slice(startIndex, endIndex);
    }, [filteredRows, currentPage, pageSize]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, debouncedSearchTerm]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Scroll table to top when page changes
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
    }, [currentPage]);

    return (
        <div className={styles.tableWrapper}>
            {isSearchDebouncing && <Loader />}
            <div className={styles.tableTopBar}>
                <div className={styles.tableTopBarSearch}>
                    <SearchInput
                        value={searchTerm}
                        placeholder="Search Unit"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: 360, maxWidth: '100%' }}
                    />
                </div>
                <div className={styles.tableTopBarFilters}>
                    <div className={styles.filterTabs}>
                        <TabGroup
                            options={datePresetOptions}
                            value={datePreset}
                            onChange={(val) => {
                                const preset = String(val);

                                // Prevent unnecessary updates if same preset is clicked
                                if (preset === datePreset) return;

                                setDatePreset(preset);

                                // For presets, immediately set the corresponding date range
                                // so the DatePicker reflects the selected filter.
                                if (preset !== 'Custom') {
                                    const [presetStart, presetEnd] = getRangeForPreset(preset);
                                    setDateRange([presetStart, presetEnd]);
                                }
                            }}
                        />
                    </div>
                    <div
                        className={`${styles.datePickerWrapper} datePicker-wrapper${datePreset !== 'Custom' ? ` ${styles.disabledDatePicker}` : ''}`}
                    >
                        <DatePicker
                            selected={startDate}
                            popperPlacement="bottom-start"
                            dateFormat="MM/dd/yyyy"
                            showPopperArrow={false}
                            placeholderText="Start Date - End Date"
                            name="Start Date"
                            className="custom-dateInput"
                            disabled={datePreset !== 'Custom'}
                            isClearable={false}
                            selectsRange={true}
                            startDate={startDate}
                            endDate={endDate}
                            onChange={(e) => {
                                setDateRange(e);
                                setDatePreset('Custom');
                            }}
                            onCalendarClose={autoSelectEndDate}
                        />
                        <img src={CalendarIcon} alt="calendar" />
                    </div>
                </div>
            </div>

            <div ref={tableContainerRef} className={styles.tableContainer}>
                <table className={styles.availabilityTable}>
                    <thead>
                        <tr>
                            <th>Unit</th>
                            <th>Locker</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Opening Time</th>
                            <th>Closing Time</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.length > 0 ? (
                            paginatedRows.map((availability: eventData, index: number) => {
                                const isDeleted = availability?.unit_status?.toLowerCase() !== 'available';
                                return (
                                    <tr
                                        key={`availability-${index}`}
                                        className={`${styles.tableRow} ${isDeleted && styles.deletedRow}`}
                                    >
                                        <td>{availability.unitYardiId || availability.unit_number || '-'}</td>
                                        <td>
                                            <span
                                                className={styles.lockerBadge}
                                                style={{ backgroundColor: getLockerColor(availability.locker_number) }}
                                            >
                                                {availability.locker_number}
                                            </span>
                                        </td>
                                        <td>{availability.start_date ? moment(availability.start_date).format('MM-DD-YYYY') : '-'}</td>
                                        <td>{availability.end_date ? moment(availability.end_date).format('MM-DD-YYYY') : '-'}</td>
                                        <td>{availability.opening_time ? moment(availability.opening_time, 'HH:mm A').format('hh:mm A') : '-'}</td>
                                        <td>
                                            {availability.closing_time === '00:00'
                                                ? '11:59 PM'
                                                : availability.closing_time ? moment(availability.closing_time, 'HH:mm A').format('hh:mm A') : '-'}
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className={styles.actionIcons}>
                                                {!isDeleted && (
                                                    <span
                                                        className={styles.iconBtn}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(availability);
                                                        }}
                                                    >
                                                        <TrashIcon />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        ) : (
                            <tr>
                                <td colSpan={7} className={styles.noData}>
                                    No availability found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {!isSearchDebouncing && filteredRows.length > 0 && (
                <div className={styles.paginationWrapper}>
                    <Pagination
                        currentPage={currentPage}
                        totalCount={filteredRows.length}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </div>
    );
};

export default AvailabilityTable;
